import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    ContentLayout,
    DatePicker,
    FormField,
    Header,
    SpaceBetween,
} from '@cloudscape-design/components';
import apiAxios from '../../client/apiClient';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}

function oneYearAgoIso(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
}

export const AdminJobsPage: React.FC = () => {
    useDocumentTitle('Admin Jobs');

    const [startDate, setStartDate] = useState(oneYearAgoIso());
    const [endDate, setEndDate] = useState(todayIso());
    const [loading, setLoading] = useState(false);
    const [picksBackfillLoading, setPicksBackfillLoading] = useState(false);
    const [minute2BackfillLoading, setMinute2BackfillLoading] = useState(false);
    const [publisherBackfillLoading, setPublisherBackfillLoading] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const handleTrigger = async () => {
        setAlert(null);
        setLoading(true);
        try {
            await apiAxios.post(`${API_BASE}/api/alpha-analysis/backfill`, null, {
                params: { startDate, endDate },
            });
            setAlert({
                type: 'success',
                message: `Backfill triggered for ${startDate} → ${endDate} — check CloudWatch logs for AlphaBackfillProd.`,
            });
        } catch (err: any) {
            const msg = err.response?.data?.error || err.message || 'Unknown error';
            setAlert({ type: 'error', message: `Trigger failed: ${msg}` });
        } finally {
            setLoading(false);
        }
    };

    const handlePicksBackfill = async () => {
        setAlert(null);
        setPicksBackfillLoading(true);
        try {
            await apiAxios.post(`${API_BASE}/api/alpha-picks/backfill`);
            setAlert({ type: 'success', message: 'Alpha Picks price backfill triggered — check CloudWatch for AlphaPicksPriceBackfillProd. Takes 15-30 min.' });
        } catch (err: any) {
            const msg = err.response?.data?.error || err.message || 'Unknown error';
            setAlert({ type: 'error', message: `Trigger failed: ${msg}` });
        } finally {
            setPicksBackfillLoading(false);
        }
    };

    const handleMinute2Backfill = async () => {
        setAlert(null);
        setMinute2BackfillLoading(true);
        try {
            await apiAxios.post(`${API_BASE}/api/alpha-picks/backfill-minute-prices`);
            setAlert({ type: 'success', message: 'Alpha Picks +1 min price backfill triggered — check CloudWatch for AlphaPicksPriceBackfillProd (mode=minute).' });
        } catch (err: any) {
            const msg = err.response?.data?.error || err.message || 'Unknown error';
            setAlert({ type: 'error', message: `Trigger failed: ${msg}` });
        } finally {
            setMinute2BackfillLoading(false);
        }
    };

    const handlePublisherMetricsBackfill = async () => {
        setAlert(null);
        setPublisherBackfillLoading(true);
        try {
            await apiAxios.post(`${API_BASE}/api/alpha-analysis/publisher-metrics/backfill`);
            setAlert({
                type: 'success',
                message: 'Publisher metrics backfill triggered — check CloudWatch for PublisherMetricsBackfillProd. The job scans every AlphaAnalysisResults row, fills missing priceAtPublish + forward-window snapshots (skipping future timestamps), then recomputes earner scores per publisher. Throttled to ~1 Polygon req/sec, so a full run can take 10+ minutes.',
            });
        } catch (err: any) {
            const msg = err.response?.data?.error || err.message || 'Unknown error';
            setAlert({ type: 'error', message: `Trigger failed: ${msg}` });
        } finally {
            setPublisherBackfillLoading(false);
        }
    };

    return (
        <ContentLayout header={<Header variant="h1">Admin Jobs</Header>}>
            <SpaceBetween size="l">
                {alert && (
                    <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
                        {alert.message}
                    </Alert>
                )}

                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Scans SeekingAlpha Pro articles in the given date range and enqueues any not yet analyzed by Claude."
                        >
                            SA Article Backfill
                        </Header>
                    }
                >
                    <SpaceBetween size="m">
                        <FormField
                            label="Start date"
                            description="Oldest articles to include (inclusive)."
                        >
                            <DatePicker
                                value={startDate}
                                onChange={({ detail }) => setStartDate(detail.value)}
                                placeholder="YYYY/MM/DD"
                            />
                        </FormField>

                        <FormField
                            label="End date"
                            description="Newest articles to include (inclusive, defaults to today)."
                        >
                            <DatePicker
                                value={endDate}
                                onChange={({ detail }) => setEndDate(detail.value)}
                                placeholder="YYYY/MM/DD"
                            />
                        </FormField>

                        <Box>
                            <Button
                                variant="primary"
                                loading={loading}
                                disabled={loading}
                                onClick={handleTrigger}
                            >
                                Trigger
                            </Button>
                        </Box>
                    </SpaceBetween>
                </Container>

                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Fetches all historical Polygon.io price data for every Alpha Pick since 2022. Takes 15-30 min. Check CloudWatch for AlphaPicksPriceBackfillProd."
                        >
                            Alpha Picks Price Backfill
                        </Header>
                    }
                >
                    <Box>
                        <Button
                            variant="primary"
                            loading={picksBackfillLoading}
                            disabled={picksBackfillLoading}
                            onClick={handlePicksBackfill}
                        >
                            Trigger
                        </Button>
                    </Box>
                </Container>

                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Backfills priceAt1MinAfterPublish for every Alpha Pick by fetching Polygon.io 1-minute bars at each pick's publish timestamp. Powers the '+1 min' entry option. Skips picks already populated. Check CloudWatch for AlphaPicksPriceBackfillProd."
                        >
                            Alpha Picks +1 Min Price Backfill
                        </Header>
                    }
                >
                    <Box>
                        <Button
                            variant="primary"
                            loading={minute2BackfillLoading}
                            disabled={minute2BackfillLoading}
                            onClick={handleMinute2Backfill}
                        >
                            Trigger
                        </Button>
                    </Box>
                </Container>

                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Scans every AlphaAnalysisResults row, fills missing priceAtPublish + 10 forward-window price snapshots (1h, 1d, 1w, 2w, 4w, 2m, 3m, 4m, 5m, 6m) via Polygon, then recomputes per-publisher earner scores. Idempotent — skips already-filled fields and future-window timestamps. Runs daily at 02:30 UTC automatically. Check CloudWatch for PublisherMetricsBackfillProd."
                        >
                            Publisher Metrics Backfill
                        </Header>
                    }
                >
                    <Box>
                        <Button
                            variant="primary"
                            loading={publisherBackfillLoading}
                            disabled={publisherBackfillLoading}
                            onClick={handlePublisherMetricsBackfill}
                        >
                            Trigger
                        </Button>
                    </Box>
                </Container>
            </SpaceBetween>
        </ContentLayout>
    );
};
